import { useState, useEffect, ReactNode, useRef } from "react";
import { v4 as uuid } from "uuid";
import clsx from "clsx";
// Helpers
import { getCellClassNameConditions, isSubTotalCell } from "@/Washouts/helpers/tables";
import { isFooterCell } from "@/Common/helpers/Tables";
import { and, nullishResolver, or } from "@/Common/helpers/sonar";
import generateTailwindVariableClasses from "@/Common/helpers/TailwindCSS";
import {
	absoluteFormatter,
	getAccountColumn,
	getGrossColumn,
	getCellClassNameFn,
	moneyRender,
} from "../../../Common/DepartmentalWashoutsTable/columns";
// Materials
import { Collapse, Divider, Grid, Stack } from "@mui/material";
import { GridAggregationModel, GridCellParams, GridValidRowModel } from "@mui/x-data-grid-premium";
// Custom components
import IconToDisplay from "@Washouts/components/tables/Common/IconToDisplay";
import TableLoader from "@/Layouts/components/Skeletons/TableLoader";
import DynamicTable from "../../../Common/DynamicTable";
import FullCollapseContainer from "@/Layouts/components/Basics/FullCollapseContainer";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
import { TypographyCell } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
// Types & constants
import {
	CarSalesTableProps,
	CustomGridGroupingColDef,
	NewCarsSalesTableDefinition,
	NewCarsSalesTableProps,
	NewCarsSalesTotals,
	NewCarsTotals,
} from "@/Washouts/types/sales";
import { DynamicColDef } from "@/Washouts/types/commons";
import { DEFAULT_HEADER_HEIGHT, ID_GRID_COLDEF } from "@/Washouts/constants";
import { StringMap } from "@/Common/interfaces/types";
import { HEADER_CLASS_NAME } from "@/Constants/tables";
import { TypographyCellProps } from "@/Layouts/components/Tables/DataSmartTable/types";
// Icons
import ChevroletLogo from "@/assets/icons/franchises/chevrolet_text.svg";
import CadillacLogo from "@/assets/icons/franchises/cadillac_text.svg";
import MitsubishiLogo from "@/assets/icons/franchises/mm.svg";
import VolvoLogo from "@/assets/icons/franchises/volvo_text.svg";
import AlfaRomeoLogo from "@/assets/icons/franchises/alfa_romeo_icon.svg";
import FiatLogo from "@/assets/icons/franchises/Fiat.svg";
import ChryslerLogo from "@/assets/icons/franchises/chrysler_icon.svg";
import DodgeLogo from "@/assets/icons/franchises/Dodge.svg";
import JeepLogo from "@/assets/icons/franchises/Jeep.svg";
import RamLogo from "@/assets/icons/franchises/Ram.svg";
import BuickLogo from "@/assets/icons/franchises/buick_text.svg";
import GMCLogo from "@/assets/icons/franchises/gmc_icon.svg";
import { measureTextWidth } from "@/Common/helpers/measure";

const SMALL_CELL_CLASS = "table-small-cell";
const HEADER_ALIGN_RIGHT = "table-header-align-right";
const HEADER_GROUPED_END_CLASS = "grouped-header-table end";

const FRANCHISE_MAP: Map<string, string> = new Map([
	["CHEVROLET", ChevroletLogo],
	["CADILLAC", CadillacLogo],
	["MITSUBISHI", MitsubishiLogo],
	["VOLVO", VolvoLogo],
	["VOLVO CARS", VolvoLogo],
	["CHRYSLER", ChryslerLogo],
	["DODGE", DodgeLogo],
	["JEEP", JeepLogo],
	["RAM", RamLogo],
	["ALFA", AlfaRomeoLogo],
	["ALFA ROMEO", AlfaRomeoLogo],
	["FIAT", FiatLogo],
	["BUICK", BuickLogo],
	["GMC", GMCLogo],
]);

const emptyColumns: DynamicColDef[] = [
	{
		field: "_id",
		headerName: "",
	},
	{
		field: "label",
		headerName: "",
		flex: 2,
		sortable: false,
		headerClassName: "show-as-column text-color",
		cellClassName: getCellClassNameConditions({
			set: [SMALL_CELL_CLASS],
		}),
		renderCell: (params) => <TypographyCell params={params}></TypographyCell>,
	},
	{
		field: "account",
		headerName: "",
		align: "right",
		flex: 1,
		sortable: false,
		headerClassName: {
			set: [HEADER_ALIGN_RIGHT],
		},
		cellClassName: getCellClassNameConditions({
			set: ["table-small-cell no-focus"],
		}),
		aggregation: "departmentalTableSum",
		renderCell: (params) => <TypographyCell params={params}>&nbsp;</TypographyCell>,
	},
	{
		field: "units",
		headerName: "",
		type: "number",
		align: "right",
		flex: 1,
		sortable: false,
		headerClassName: HEADER_ALIGN_RIGHT,
		cellClassName: getCellClassNameConditions({
			set: [SMALL_CELL_CLASS],
		}),
		renderCell: (params) => <TypographyCell params={params}>&nbsp;</TypographyCell>,
	},
	{
		flex: 1,
		field: "grossProfit",
		headerName: "",
		type: "number",
		minWidth: 120,
		sortable: false,
		align: "right",
		aggregation: "departmentalTableSum",
		headerClassName: HEADER_ALIGN_RIGHT,
		cellClassName: getCellClassNameConditions({
			set: [SMALL_CELL_CLASS],
		}),
		renderCell: (params) => (
			<TypographyCell params={params} hints={{ isMoney: false }}>
				&nbsp;
			</TypographyCell>
		),
	},
];

function getRowsFromData(rows: Record<string, object>[]) {
	const isObject = (obj: object) => typeof obj === "object" && obj !== null;
	const getKey = (before: string, after: string) => {
		if (before === "value") {
			return after;
		}
		return before + String(after?.[0]?.toUpperCase()) + after?.substring?.(1);
	};

	const newRows: Record<string, object | string>[] = [];
	rows?.forEach?.((row) => {
		const rowToBuild: StringMap<object> = {};
		Object.entries(row ?? {})?.forEach?.(([objectKey, objectValue]) => {
			if (objectKey === "display") {
				// @ts-ignore
				rowToBuild[objectKey] = getRowsFromData(objectValue);
			} else if (!isObject(objectValue)) {
				rowToBuild[objectKey] = objectValue;
			} else if (Array.isArray(objectValue)) {
				rowToBuild[objectKey] = objectValue;
			} else {
				Object.entries(objectValue ?? {})?.forEach?.(([key, value]) => {
					if (key === "value") {
						rowToBuild[objectKey] = value;
					}
					rowToBuild[getKey(key, objectKey)] = value;
				});
			}
		});
		newRows.push(rowToBuild);
	});
	let newRowCount = 0;
	newRows.forEach((row) => {
		if (!row?._id) {
			row._id = `oem-${newRowCount}`;
			newRowCount += 1;
		}
		if (row?.id) {
			row.id = row._id;
		}
	});
	return newRows;
}

function getFooterModelsFromColumns(columns: DynamicColDef[] = []) {
	const aggregations: StringMap<string> = {};
	columns.forEach((column) => {
		if (column?.aggregation && column?.field) {
			aggregations[column.field] = column.aggregation;
		}
	});
	return aggregations;
}

function getFranchiseLogo(franchise: string) {
	return FRANCHISE_MAP.get(String(franchise).toUpperCase()) ?? null;
}

const getMaxRows = (display: GridValidRowModel[] = []): number => {
	if (display?.some?.((row) => row?.page)) {
		const maxRows = display?.filter?.((row) => row?.page);
		let maxRowsLength = 0;
		maxRows?.forEach?.((row) => {
			if (row?.display?.length > maxRowsLength) {
				maxRowsLength = row?.display?.length;
			}
		});
		return maxRowsLength;
	}
	return display?.length ?? 0;
};

function evenRowCount(table1: NewCarsSalesTableDefinition, table2: NewCarsSalesTableDefinition, rowCount: number) {
	const t1Length = getMaxRows(table1?.display);
	const t2Length = getMaxRows(table2?.display);
	const maxRows = Math.max(t1Length, t2Length);
	[table1?.display, table2?.display]
		.filter((display: GridValidRowModel[] | undefined) => Boolean(display))
		.forEach((display: GridValidRowModel[] | undefined) => {
			const pages = display?.filter?.((entry) => entry?.page);
			if (pages?.length) {
				pages?.forEach?.((page) => {
					const diff = maxRows - page?.display?.length;
					for (let i = 0; i < diff; i++) {
						page.display.push({ emptyRow: true, _id: "synchronized-row-" + rowCount });
						rowCount++;
					}
				});
			} else {
				const diff = maxRows - (display as GridValidRowModel[])?.length;
				for (let i = 0; i < diff; i++) {
					display?.push?.({ emptyRow: true, _id: "synchronized-row-" + rowCount });
					rowCount++;
				}
			}
		});
	return { table1, table2, rowCount };
}

function synchronizeTables(tables: NewCarsSalesTableDefinition[]) {
	let newRowCount = 0;
	const syncedTables = [...tables];
	for (let i = 0; i < syncedTables.length; i += 2) {
		let table1 = syncedTables?.[i];
		let table2 = syncedTables?.[i + 1];
		if (!table2) {
			table2 = {
				franchise: "empty",
				display: [],
				properties: {
					...nullishResolver(table1?.properties, {}),
					hasRetailAndLeaseSplit: checkForRetailAndLeaseSplit(getRowsFromData(extractAllRowsToOneArray(table1?.display ?? []))),
				},
			};
			syncedTables.push(table2);
		}
		const result = evenRowCount(table1, table2, newRowCount);
		table1 = result.table1;
		table2 = result.table2;
		newRowCount = result.rowCount;
		const maxFranchisesToShow = Math.max(
			nullishResolver(table1?.properties?.franchisesToBeShown, 0),
			nullishResolver(table2?.properties?.franchisesToBeShown, 0)
		);
		if (table1.properties) {
			table1.properties.franchisesToBeShown = maxFranchisesToShow;
		}
		if (table2.properties) {
			table2.properties.franchisesToBeShown = maxFranchisesToShow;
		}
	}
	return syncedTables;
}

function checkForRetailAndLeaseSplit(rows: GridValidRowModel[]) {
	const result = rows.find(
		(row) =>
			Boolean(row["retailGrossProfit"]) ||
			Boolean(row["leaseGrossProfit"]) ||
			row?.display?.some?.((nestedRow: GridValidRowModel) => {
				return Boolean(nestedRow?.["retailGrossProfit"]) || Boolean(nestedRow?.["leaseGrossProfit"]);
			})
	);
	return Boolean(result);
}

function getColumnGroupingModel(hasRetailAndLeaseSplit: boolean) {
	const COMMON_CLASSNAME = clsx({
		"group-main-header": true,
		"[&_.MuiDataGrid-columnHeaderTitle]:font-montserrat": true,
		"[&_.MuiDataGrid-columnHeaderTitle]:font-[500]": true,
		"[&_.MuiDataGrid-columnHeaderTitle]:text-[color:--text-color]": true,
		"[&_.MuiDataGrid-columnHeaderTitle]:text-[16px]": true,
	});
	const groupingModel: CustomGridGroupingColDef[] = [
		{
			groupId: "empty",
			headerName: "",
			headerClassName: hasRetailAndLeaseSplit ? `${COMMON_CLASSNAME} ${RIGHT_BORDERED_CELL}` : `${COMMON_CLASSNAME}`,
			headerAlign: "center",
			children: [{ field: "label" }, { field: "account" }, { field: "units" }, { field: "grossProfit" }],
		},
		{
			groupId: "units",
			headerName: "Units",
			headerClassName: hasRetailAndLeaseSplit ? `${COMMON_CLASSNAME} ${RIGHT_BORDERED_CELL}` : `${COMMON_CLASSNAME}`,
			headerAlign: "center",
			children: [{ field: "retailUnits" }, { field: "leaseUnits" }],
		},
		{
			groupId: "grossProfit",
			headerName: "Gross Profit",
			width: "100%",
			headerClassName: hasRetailAndLeaseSplit ? `${COMMON_CLASSNAME} ${RIGHT_BORDERED_CELL}` : `${COMMON_CLASSNAME}`,
			headerAlign: "center",
			children: [{ field: "retailGrossProfit" }, { field: "leaseGrossProfit" }],
		},
	];
	return groupingModel;
}

const RIGHT_BORDERED_CELL = "right-inset";

function getLabelColumn(hasRetailAndLeaseSplit: boolean): DynamicColDef {
	const MIN_WIDTH_SPLIT = 165;
	const MIN_WIDTH_NORMAL = 280;
	const FONT_SIZE_PX = 12;
	const DEFAULT_CELL_HORIZONTAL_PADDING = 10;
	const widthToUse = hasRetailAndLeaseSplit ? MIN_WIDTH_SPLIT : MIN_WIDTH_NORMAL;
	return {
		field: "label",
		headerName: "Models",
		flex: 1,
		minWidth: widthToUse,
		width: widthToUse,
		sortable: false,
		align: "left",
		headerClassName: HEADER_CLASS_NAME,
		renderHeader: (params) => <TypographyHeader params={params} />,
		cellClassName: getCellClassNameConditions({}),
		renderCell: (params) => {
			let content = params?.formattedValue || params.value;
			if (isFooterCell(params)) {
				content = "Total New Front Gross";
			} else if (params && isSubTotalCell(params.row)) {
				content = params.row?.label ?? "";
			}
			const fontToUse = or(isFooterCell(params), isSubTotalCell(params.row)) ? "Montserrat" : "PTSansProNarrow";
			const weightToUse = or(isFooterCell(params), isSubTotalCell(params.row)) ? "bold" : "normal";
			const textWidth = measureTextWidth(String(content), FONT_SIZE_PX, fontToUse, weightToUse);
			const tooltipProps: TypographyCellProps["tooltipProps"] =
				textWidth > (params.colDef.width ?? widthToUse) - 2 * DEFAULT_CELL_HORIZONTAL_PADDING
					? {
							contentAsTitle: true,
					  }
					: undefined;
			return (
				<TypographyCell params={params} tooltipProps={tooltipProps}>
					{content}
				</TypographyCell>
			);
		},
	};
}

function getUnitsColumn(): DynamicColDef {
	return {
		field: "units",
		headerName: "Units",
		type: "number",
		aggregation: "departmentalTableSum",
		align: "right",
		width: 60,
		resizable: false,
		sortable: false,
		headerClassName: HEADER_CLASS_NAME,
		renderHeader: (params) => <TypographyHeader params={params} right />,
		cellClassName: getCellClassNameConditions({}),
		renderCell: (params) => <TypographyCell params={params} />,
		valueFormatter: (value, row) => {
			if (!row?.emptyRow) {
				return value ? parseInt(value, 10) : "0";
			}
			return undefined;
		},
	};
}

function getRetailUnitsColumn(): DynamicColDef {
	return {
		field: "retailUnits",
		headerName: "Retail Units",
		type: "number",
		aggregation: "departmentalTableSum",
		align: "center",
		width: 57,
		resizable: false,
		sortable: false,
		headerClassName: HEADER_CLASS_NAME,
		renderHeader: (params) => <TypographyHeader params={params} right />,
		cellClassName: getCellClassNameConditions({}),
		renderCell: (params) => <TypographyCell params={params} />,
		valueFormatter: (value, row) => {
			if (!row?.emptyRow) {
				return value ? parseInt(value, 10) : "0";
			}
			return undefined;
		},
	};
}

function getLeaseUnitsColumn(hasRetailAndLeaseSplit: boolean): DynamicColDef {
	return {
		field: "leaseUnits",
		headerName: "Lease Units",
		type: "number",
		aggregation: "departmentalTableSum",
		align: "center",
		width: 57,
		resizable: false,
		sortable: false,
		headerClassName: {
			set: [HEADER_CLASS_NAME],
			conditions: [
				{
					className: RIGHT_BORDERED_CELL,
					condition: (params) => Boolean(hasRetailAndLeaseSplit) && !isFooterCell(params as GridCellParams),
				},
			],
		},
		renderHeader: (params) => <TypographyHeader params={params} right />,
		cellClassName: getCellClassNameConditions({
			conditions: [
				{
					className: `${HEADER_GROUPED_END_CLASS} ${RIGHT_BORDERED_CELL}`,
					condition: (params) => Boolean(hasRetailAndLeaseSplit) && !isFooterCell(params),
				},
			],
		}),
		renderCell: (params) => <TypographyCell params={params} />,
		valueFormatter: (value, row) => {
			if (!row?.emptyRow) {
				return value ? parseInt(value, 10) : "0";
			}
			return undefined;
		},
	};
}

function getRetailGrossColumn(hasRetailAndLeaseSplit: boolean): DynamicColDef {
	const MIN_WIDTH_SPLIT = 100;
	const MIN_WIDTH_NORMAL = 120;
	return {
		field: "retailGrossProfit",
		headerName: "Retail Gross ($)",
		type: "number",
		aggregation: "departmentalTableSum",
		sortable: false,
		align: "right",
		minWidth: hasRetailAndLeaseSplit ? MIN_WIDTH_SPLIT : MIN_WIDTH_NORMAL,
		resizable: false,
		headerClassName: HEADER_CLASS_NAME,
		renderHeader: (params) => <TypographyHeader params={params} left />,
		cellClassName: getCellClassNameFn(false),
		renderCell: moneyRender,
		valueFormatter: absoluteFormatter,
	};
}

function getLeaseGrossColumn(hasRetailAndLeaseSplit: boolean): DynamicColDef {
	const MIN_WIDTH_SPLIT = 100;
	const MIN_WIDTH_NORMAL = 120;
	return {
		field: "leaseGrossProfit",
		headerName: "Lease Gross ($)",
		type: "number",
		aggregation: "departmentalTableSum",
		sortable: false,
		align: "right",
		minWidth: hasRetailAndLeaseSplit ? MIN_WIDTH_SPLIT : MIN_WIDTH_NORMAL,
		resizable: false,
		headerClassName: {
			set: [HEADER_CLASS_NAME],
			conditions: [
				{
					className: RIGHT_BORDERED_CELL,
					condition: (params) => Boolean(hasRetailAndLeaseSplit) && !isFooterCell(params as GridCellParams),
				},
			],
		},
		renderHeader: (params) => <TypographyHeader params={params} left />,
		cellClassName: getCellClassNameFn(hasRetailAndLeaseSplit),
		renderCell: moneyRender,
		valueFormatter: absoluteFormatter,
	};
}

function getColumnsMap(hasRetailAndLeaseSplit: boolean): Array<[string, DynamicColDef]> {
	return [
		["id", ID_GRID_COLDEF as DynamicColDef],
		["label", getLabelColumn(hasRetailAndLeaseSplit)],
		["account", getAccountColumn(hasRetailAndLeaseSplit, false, true)],
		["units", getUnitsColumn()],
		["retailUnits", getRetailUnitsColumn()],
		["leaseUnits", getLeaseUnitsColumn(hasRetailAndLeaseSplit)],
		["grossProfit", getGrossColumn(hasRetailAndLeaseSplit, false)],
		["retailGrossProfit", getRetailGrossColumn(hasRetailAndLeaseSplit)],
		["leaseGrossProfit", getLeaseGrossColumn(hasRetailAndLeaseSplit)],
		[
			"icon",
			{
				field: "icon",
				headerName: "",
				type: "string",
				align: "left",
				width: 38,
				minWidth: 38,
				maxWidth: 38,
				resizable: false,
				sortable: false,
				filterable: false,
				headerClassName: HEADER_CLASS_NAME,
				renderHeader: (params) => <TypographyHeader params={params} />,
				cellClassName: getCellClassNameConditions({
					set: ["!px-[4px]"],
				}),
				renderCell: (params) => <IconToDisplay params={params} />,
			},
		],
	];
}

function LogoSection({
	franchise,
	className = "",
	height = 40,
	updateHeight = () => {},
}: Readonly<{ franchise: string; className?: string; height?: number; updateHeight?: (height: number) => void }>) {
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const currentHeight = ref.current?.clientHeight;
		if (currentHeight && currentHeight > height) {
			updateHeight(currentHeight);
		}
	}, [ref, height, updateHeight]);

	let franchiseList: string[] = [];
	if (franchise.includes("/")) {
		franchiseList = franchise.split("/");
	} else {
		franchiseList = franchise.split(" ");
	}
	const logos: ReactNode[] = [];
	let logo: string | null = null;
	for (const franchiseName of franchiseList) {
		logo = getFranchiseLogo(franchiseName);
		if (logo) {
			logos.push(
				<img
					key={`${franchiseName}`}
					alt={`${franchiseName} franchise logo`}
					src={logo}
					className="min-h-[40px] max-h-[40px] max-w-[145px]"
				/>
			);
		}
	}
	return (
		<Grid
			container
			component={"div"}
			ref={ref}
			className={`${className} flex flex-row flex-wrap gap-x-[24px] gap-y-[8px] min-h-[--logos-min-height]`}
			style={generateTailwindVariableClasses("--logos-min-height", `${height}px`)}
		>
			{...logos}
		</Grid>
	);
}

const getPagesLength = (tableInformation: NewCarsSalesTableDefinition) => {
	const display = tableInformation.display;
	const pages = display?.filter?.((entry) => entry?.page);
	return pages?.length ? pages?.length : 1;
};

const getPages = (tableInformation: NewCarsSalesTableDefinition) => {
	const display = tableInformation.display;
	const pages = display?.filter?.((entry) => entry?.page);
	return pages?.length ? pages : display;
};

const getDefaultPageIndex = (tableInformation: NewCarsSalesTableDefinition) =>
	Math.max(0, tableInformation?.display?.findIndex?.((entry) => entry.isDefaultPage) ?? 0);

function CarSalesTable(props: Readonly<CarSalesTableProps>) {
	const { tableInformation } = props;
	const franchise = nullishResolver(tableInformation?.franchise, "empty");
	const hasRetailAndLeaseSplit = Boolean(tableInformation?.properties?.hasRetailAndLeaseSplit);
	const display = nullishResolver(getPages(tableInformation), []);
	const pagesLength = getPagesLength(tableInformation);
	const emptyTable = franchise === "empty";

	const [footerModels, setFooterModels] = useState<GridAggregationModel>({});
	const [rows, setRows] = useState<GridValidRowModel[]>([]);
	const [columns, setColumns] = useState<DynamicColDef[]>([]);
	const [activePage, setActivePage] = useState(getDefaultPageIndex(tableInformation));

	const columnGroupingModel = getColumnGroupingModel(!emptyTable && hasRetailAndLeaseSplit);

	const newCarSalesTableColumnsMap = new Map(getColumnsMap(hasRetailAndLeaseSplit));

	const getColumnById = (id: string) => {
		const sanitize = String(id);
		let attributes = nullishResolver(newCarSalesTableColumnsMap.get(sanitize), null);

		if (attributes && (id === "contracts" || id === "units")) {
			attributes = {
				...attributes,
				type: "number",
				aggregation: "departmentalTableSum",
			};
		}
		return attributes;
	};

	const validColumns = (columnNames: string[]): DynamicColDef[] => {
		const columnsAux: DynamicColDef[] = [];
		let column: DynamicColDef | null = null;
		columnNames.forEach((columnName) => {
			column = getColumnById(columnName);
			if (column) {
				columnsAux.push(column);
			}
		});
		return columnsAux;
	};

	useEffect(() => {
		const columnsAux = validColumns(["label", "account"]);
		if (hasRetailAndLeaseSplit) {
			columnsAux.push(...validColumns(["retailUnits", "leaseUnits", "retailGrossProfit", "leaseGrossProfit"]));
		} else {
			columnsAux.push(...validColumns(["units", "grossProfit"]));
		}
		columnsAux.push(...validColumns(["icon"]));

		setColumns(columnsAux);

		setRows(display);
		setFooterModels(!emptyTable ? getFooterModelsFromColumns(columnsAux) : {});

		return () => {
			setColumns([]);
			setRows([]);
			setFooterModels({});
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props]);

	if (!columns || !footerModels) {
		return null;
	}

	return (
		<Stack>
			<LogoSection
				className="mt-[5px]"
				franchise={franchise}
				height={props.minFranchiseLogosHeight}
				updateHeight={props.updateHeight}
			/>
			<DynamicTable
				{...props}
				tableRef="newCarsSales"
				headerHeight={DEFAULT_HEADER_HEIGHT}
				columns={emptyTable ? emptyColumns : columns}
				data={pagesLength > 1 ? getRowsFromData([...(display?.[activePage]?.display ?? [])]) : rows}
				columnGroupingModel={hasRetailAndLeaseSplit ? columnGroupingModel : undefined}
				title={{
					hide: true,
				}}
				headerClassName={HEADER_CLASS_NAME}
				subFooterClassName={"bg-[#b3b3b3]"}
				columnVisibilityModel={{
					_id: false,
				}}
				negatives={["units", "retailUnits", "leaseUnits", "grossProfit", "retailGrossProfit", "leaseGrossProfit", "account"]}
				disableToolbar={emptyTable}
				customProps={{
					tableClassName: {
						"[&_.MuiDataGrid-toolbarContainer]:min-h-[42px] [&_.MuiDataGrid-toolbarContainer]:mb-[5px]": true,
						"[&_.toolbar]:absolute [&_.toolbar-actions]:right-0 [&_.toolbar-actions]:top-[6px]": true,
						"[&_.MuiDataGrid-columnHeaders_*]:!border-none": true,
					},
				}}
				pageSelector={{
					pagesLength,
					activePage,
					present: pagesLength > 1,
					onActionUpdate: (page: number) => setActivePage(page),
					activePageTitle: display?.[activePage]?.title,
					pages: display,
				}}
			/>
		</Stack>
	);
}

function deriveTotals(totals: { values?: NewCarsTotals } | null): NewCarsSalesTotals {
	if (totals?.values) {
		const totalsAux = getRowsFromData([nullishResolver(totals?.["values"], {})]);
		if (totalsAux?.length) {
			totals = nullishResolver(totalsAux.pop(), null);
		} else {
			totals = null;
		}
	} else {
		totals = null;
	}
	return totals as NewCarsSalesTotals;
}

function extractAllRowsToOneArray(rows: GridValidRowModel[]): GridValidRowModel[] {
	const rowsAux: GridValidRowModel[] = [];
	rows.forEach((row) => {
		if (row?.display?.length) {
			row.display.forEach((display: GridValidRowModel) => {
				rowsAux.push(display);
			});
		} else {
			rowsAux.push(row);
		}
	});
	return rowsAux;
}

export default function NewCarSalesTable(props: Readonly<NewCarsSalesTableProps>) {
	const [tables, setTables] = useState<NewCarsSalesTableDefinition[]>([]);
	const [franchiseLogosHeight, setFranchiseLogosHeight] = useState(0);

	const { data, loading } = props;

	useEffect(() => {
		let placeholder: NewCarsSalesTableDefinition[] = [{ display: [] }, { display: [] }];
		if (data?.length) {
			const tablesAux: NewCarsSalesTableDefinition[] = [];
			data?.forEach?.((newCarsSalesOEM) => {
				newCarsSalesOEM?.franchises?.forEach?.((franchiseEntry) => {
					const rows = getRowsFromData(nullishResolver(franchiseEntry?.display, []));
					let totals = nullishResolver(
						franchiseEntry?.groups?.find?.((groupEntry) => groupEntry?.group === "totals"),
						null
					);
					if (and(!totals, Boolean(franchiseEntry?.totals))) {
						totals = { values: franchiseEntry?.totals };
					}
					tablesAux.push({
						totals: deriveTotals(totals),
						franchise: franchiseEntry?.franchise,
						franchises: nullishResolver(franchiseEntry?.franchises, []),
						display: rows,
						properties: {
							hasRetailAndLeaseSplit: checkForRetailAndLeaseSplit(
								getRowsFromData(extractAllRowsToOneArray(franchiseEntry?.display))
							),
							franchisesToBeShown: franchiseEntry?.franchises?.length,
						},
					});
				});
			});
			placeholder = synchronizeTables(tablesAux);
		}
		setTables(placeholder);
	}, [data, loading]);

	const divider = <Divider className="!ml-[20px] !mr-[20px] !border-b !border-[#CCC]"></Divider>;

	return (
		<Stack>
			{loading ? (
				<>
					<Grid className="py-[20px] px-[32px]">
						<TableLoader smallTitle toolbar />
					</Grid>
					{divider}
				</>
			) : (
				<>
					{props?.dataPresent ? (
						<>
							<FullCollapseContainer title={"New Cars Sales"} arrow={true}>
								<Grid container className="grid grid-cols-2 gap-[48px] pl-[30px] pb-[32px]">
									{tables.map((table) => (
										<Grid key={`${table?.franchise}-${uuid()}`} item className="w-[646px]">
											{props.dataPresent && (
												<CarSalesTable
													{...props}
													tableInformation={table}
													minFranchiseLogosHeight={franchiseLogosHeight}
													updateHeight={setFranchiseLogosHeight}
												/>
											)}
										</Grid>
									))}
								</Grid>
							</FullCollapseContainer>
							<Collapse in={true} collapsedSize={0} timeout={100}>
								{divider}
							</Collapse>
						</>
					) : (
						<></>
					)}
				</>
			)}
		</Stack>
	);
}
